import { ArrowDown, ArrowUp, Link, Plus, Trash2 } from "lucide-react";
import type { ButtonLayout, CardButton } from "../lib/types";
import { getButtonTextError, getButtonUrlError, MAX_BUTTONS } from "../lib/validators";

interface ButtonBuilderProps {
  buttons: CardButton[];
  layout: ButtonLayout;
  onButtonsChange: (buttons: CardButton[]) => void;
  onLayoutChange: (layout: ButtonLayout) => void;
}

function createButton(): CardButton {
  return {
    id: crypto.randomUUID(),
    text: "",
    url: ""
  };
}

export function ButtonBuilder({ buttons, layout, onButtonsChange, onLayoutChange }: ButtonBuilderProps) {
  const updateButton = (id: string, patch: Partial<CardButton>) => {
    onButtonsChange(buttons.map((button) => (button.id === id ? { ...button, ...patch } : button)));
  };

  const moveButton = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= buttons.length) return;
    const next = [...buttons];
    const [button] = next.splice(index, 1);
    next.splice(nextIndex, 0, button);
    onButtonsChange(next);
  };

  const removeButton = (id: string) => onButtonsChange(buttons.filter((button) => button.id !== id));

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Button Builder</h2>
          <p className="text-sm text-slate-500">1 ปุ่มมี 1 URL เท่านั้น และรองรับ http://, https://, t.me/</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-line bg-white p-1">
            {[1, 2].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onLayoutChange(value as ButtonLayout)}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  layout === value ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {value === 1 ? "1 per row" : "2 per row"}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onButtonsChange([...buttons, createButton()])}
            disabled={buttons.length >= MAX_BUTTONS}
            className="inline-flex items-center gap-2 rounded bg-ink px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
            Add Button
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {buttons.length === 0 ? (
          <div className="rounded border border-line bg-white p-4 text-sm text-slate-500">
            ยังไม่มีปุ่ม กด Add Button เพื่อเพิ่มปุ่มใน Telegram card
          </div>
        ) : null}

        {buttons.map((button, index) => {
          const textError = button.text.trim()
            ? getButtonTextError(button.text)
            : button.url.trim()
              ? "Button text is required"
              : null;
          const urlError = button.url.trim()
            ? getButtonUrlError(button.url)
            : button.text.trim()
              ? "Button URL is required"
              : null;

          return (
            <div key={button.id} className="rounded border border-line bg-white p-3">
              <div className="grid gap-3 md:grid-cols-[1fr_1.4fr_auto]">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Button text</span>
                  <input
                    value={button.text}
                    onChange={(event) => updateButton(button.id, { text: event.target.value })}
                    className={`focus-ring w-full rounded border px-3 py-2 text-sm ${textError ? "border-red-300" : "border-line"}`}
                    placeholder="เช่น สมัครสมาชิก"
                    maxLength={64}
                  />
                  {textError ? <span className="mt-1 block text-xs text-red-600">{textError}</span> : null}
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Button URL</span>
                  <div className="relative">
                    <Link className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      value={button.url}
                      onChange={(event) => updateButton(button.id, { url: event.target.value })}
                      className={`focus-ring w-full rounded border px-3 py-2 pl-9 text-sm ${urlError ? "border-red-300" : "border-line"}`}
                      placeholder="https://example.com หรือ t.me/example"
                    />
                  </div>
                  {urlError ? (
                    <span className="mt-1 block text-xs text-red-600">{urlError}</span>
                  ) : (
                    <span className="mt-1 block text-xs text-slate-500">Only one URL per button</span>
                  )}
                </label>

                <div className="flex items-center justify-end gap-1 self-end">
                  <button
                    type="button"
                    onClick={() => moveButton(index, -1)}
                    className="rounded border border-line p-2 text-slate-600 hover:bg-slate-100"
                    title="Move Up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveButton(index, 1)}
                    className="rounded border border-line p-2 text-slate-600 hover:bg-slate-100"
                    title="Move Down"
                  >
                    <ArrowDown size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeButton(button.id)}
                    className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"
                    title="Delete Button"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
